import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getAnonymousId } from "./anonymousId";
import { track } from "./tracker";
import { getMyLeadProfile } from "@/lib/identity.functions";

type LeadProfile = {
  identified: boolean;
  phone?: string | null;
  email?: string | null;
  lastProductHandle?: string | null;
  lastRoute?: string | null;
  brand?: string | null;
};

type IdentityCtx = {
  anonymousId: string;
  profile: LeadProfile | null;
  isIdentified: boolean;
  refresh: () => void;
  track: typeof track;
};

const Ctx = createContext<IdentityCtx | null>(null);

export function IdentityContextProvider({ children }: { children: ReactNode }) {
  const [anonymousId, setAnonymousId] = useState("");
  const [profile, setProfile] = useState<LeadProfile | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = getAnonymousId();
    setAnonymousId(id);
  }, []);

  useEffect(() => {
    if (!anonymousId) return;
    let cancel = false;
    getMyLeadProfile({ data: { anonymousId } })
      .then((p) => {
        if (!cancel) setProfile(p as LeadProfile);
      })
      .catch(() => {
        if (!cancel) setProfile({ identified: false });
      });
    return () => {
      cancel = true;
    };
  }, [anonymousId, tick]);

  const value = useMemo<IdentityCtx>(
    () => ({
      anonymousId,
      profile,
      isIdentified: !!profile?.identified,
      refresh: () => setTick((t) => t + 1),
      track,
    }),
    [anonymousId, profile],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useIdentity() {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Safe fallback for components rendered before provider (SSR or strict trees).
    return {
      anonymousId: "",
      profile: null,
      isIdentified: false,
      refresh: () => undefined,
      track,
    } satisfies IdentityCtx;
  }
  return ctx;
}
